/**
 * useWordPopover — a self-contained state machine for the word-lookup popover
 * used by both the Reader (texts) and the Video Reader (subtitles).
 *
 * Responsibilities
 * ────────────────
 *   • open / close the popover for a token or a free-text term (synonym click)
 *   • track loading state while fetching lexeme data
 *   • maintain a "back" history stack so clicking a related word can be undone
 *   • auto-dismiss on window resize (popover position becomes invalid)
 *   • detect multi-word text selection and treat it as an expression lookup
 *   • fetch note / translation / cached audio state for the current entry
 *
 * What it *doesn't* do
 * ────────────────────
 *   • render anything — callers render <WordPopover {...popover.bindProps} />
 *   • own optimistic word-status updates (keep that in each page so optimism
 *     can coexist with page-specific state like adaptive/sentence tokens)
 *   • own TTS — caller provides its own play handler
 *
 * Return shape
 * ────────────
 *   {
 *     open: (token, event) => void        // used on token click
 *     openForTerm: (term, event) => void  // used on synonym / collocation click
 *     close: () => void
 *     back: () => void
 *     canGoBack: boolean,
 *     popoverPos, wordData, wordLoading,  // rendering props
 *     selectedToken, isExpression,        // extra context
 *     note, setNote, translation, setTranslation,
 *     translationSaved, setTranslationSaved,
 *     hasCachedAudio, setHasCachedAudio,
 *     isNewEntry, setIsNewEntry,
 *     refreshCurrent: () => Promise<void>,
 *   }
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { vocabularyApi } from '../../utils/api';
import { computePopoverPosition } from './popoverPosition';
import { detectExpressionFromSelection } from './selection';
import { pushRecentLookup } from './recentLookups';
import { useWordEntry } from './useWordEntry';

export function useWordPopover({ languageCode, textId = null, interfaceLang = 'en' } = {}) {
  // ── Popover state ──
  const [selectedToken, setSelectedToken] = useState(null);
  const { wordData, setWordData, wordLoading, loadEntry, cancel } = useWordEntry({ languageCode, interfaceLang });
  const [popoverPos, setPopoverPos] = useState(null);
  const [isExpression, setIsExpression] = useState(false);

  // ── Ancillary editable state the popover owns ──
  const [note, setNote] = useState('');
  const [translation, setTranslation] = useState('');
  const [translationSaved, setTranslationSaved] = useState(false);
  const [hasCachedAudio, setHasCachedAudio] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(true);

  // ── Back stack ──
  const [history, setHistory] = useState([]);

  // Keep the last fetched term so `refreshCurrent` can re-fire the fetch.
  const currentTermRef = useRef(null);
  const currentIsExprRef = useRef(false);
  const currentLanguageRef = useRef(languageCode);
  const requestRef = useRef(0);

  useEffect(() => {
    currentLanguageRef.current = languageCode;
    setSelectedToken(null);
    setPopoverPos(null);
    return () => { requestRef.current += 1; };
  }, [languageCode, interfaceLang]);

  const close = useCallback(() => {
    requestRef.current += 1;
    cancel();
    setSelectedToken(null);
    setWordData(null);
    setPopoverPos(null);
    setIsExpression(false);
    setHistory([]);
    currentTermRef.current = null;
    currentIsExprRef.current = false;
  }, [cancel, setWordData]);

  // Close on viewport resize — position math is anchored to a rect that
  // becomes invalid the moment the layout shifts.
  useEffect(() => {
    if (!popoverPos) return;
    const onResize = () => close();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [popoverPos, close]);

  const _fetchEntry = useCallback(
    async (term, expr, lookupLanguage = languageCode) => {
      if (!term) return;
      currentTermRef.current = term;
      currentIsExprRef.current = expr;
      currentLanguageRef.current = lookupLanguage;

      const request = ++requestRef.current;
      setNote('');
      setTranslation('');
      setTranslationSaved(false);
      setHasCachedAudio(false);
      setIsNewEntry(true);

      // Fire-and-forget exposure tracking
      try {
        vocabularyApi
          .trackExposures([
            {
              word: term,
              language_code: lookupLanguage,
              event_type: 'clicked',
              ...(textId ? { text_id: textId } : {}),
            },
          ])
          .catch(() => {});
      } catch {}

      // Audio availability never blocks the dictionary or enrichment.
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      fetch(`${BACKEND_URL}/api/tts/cache/check?word=${encodeURIComponent(term)}&language=${encodeURIComponent(lookupLanguage)}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (request === requestRef.current) setHasCachedAudio(!!data?.cached); })
        .catch(() => {});

      const data = await loadEntry(term, expr, lookupLanguage);
      if (data && request === requestRef.current) {
        setNote(data?.user_state?.notes || '');
        setTranslation(data?.user_state?.translation || '');

        // Record in recent-lookups MRU after a successful fetch
        pushRecentLookup({ term, lang: lookupLanguage });

        const hasExisting =
          data?.lexeme_id ||
          (data?.user_state?.status && data.user_state.status !== 'new') ||
          (data?.definitions?.length || 0) > 0;
        setIsNewEntry(!hasExisting);

      }
    },
    [languageCode, textId, loadEntry]
  );

  // ── Entry point when the user clicks a token ──
  const open = useCallback(
    (token, event, options = {}) => {
      if (!token?.is_word) return;
      setHistory([]);  // Fresh click clears back stack

      const forcedPhrase = options.forceExpression;

      // Multi-word selection?
      const sel = typeof window !== 'undefined' ? window.getSelection()?.toString() : '';
      let isExpr, term, displayTerm;
      if (forcedPhrase) {
        isExpr = true;
        term = forcedPhrase.toLowerCase();
        displayTerm = forcedPhrase;
      } else {
        ({ isExpression: isExpr, term, displayTerm } = detectExpressionFromSelection(sel, token));
      }
      if (isExpr && typeof window !== 'undefined') window.getSelection()?.removeAllRanges();

      // Position
      const rect = event?.currentTarget?.getBoundingClientRect?.();
      const pos = computePopoverPosition(rect);
      if (!pos) return;
      setPopoverPos(pos);

      setIsExpression(isExpr);
      const tokenLike = isExpr
        ? { ...token, normalized_form: term, surface_form: displayTerm, is_expression: true }
        : token;
      setSelectedToken(tokenLike);

      _fetchEntry(term, isExpr, token.language_code || languageCode);
    },
    [_fetchEntry, languageCode]
  );

  // ── Entry point when a related-word chip inside the popover is clicked ──
  const openForTerm = useCallback(
    (term) => {
      const raw = (term || '').trim();
      if (!raw) return;
      if (!popoverPos) {
        setPopoverPos(computePopoverPosition({ left: window.innerWidth / 2, right: window.innerWidth / 2, top: window.innerHeight / 2, bottom: window.innerHeight / 2 }));
      }

      // Push current state onto history so back() can restore it
      if (selectedToken && wordData) {
        setHistory((h) => [
          ...h,
          {
            token: selectedToken,
            wordData,
            isExpression,
            note,
            translation,
            translationSaved,
          },
        ]);
      }

      const isMulti = raw.includes(' ');
      const synthetic = {
        token_id: `synthetic-${raw}-${Date.now()}`,
        normalized_form: raw.toLowerCase(),
        surface_form: raw,
        is_word: true,
        status: 'new',
        strength: 0,
        language_code: currentLanguageRef.current || languageCode,
      };
      setIsExpression(isMulti);
      setSelectedToken(synthetic);

      _fetchEntry(raw.toLowerCase(), isMulti, currentLanguageRef.current || languageCode);
    },
    [_fetchEntry, selectedToken, wordData, isExpression, note, translation, translationSaved, popoverPos, languageCode]
  );

  // Entry point for standalone popovers (command palette, search, etc.) that
  // don't have a DOM anchor. Same fetch behaviour as openForTerm but also
  // forces a default popoverPos so the shared <WordPopover> will render.
  const openStandalone = useCallback(
    (term, positionOverride = null) => {
      const raw = (term || '').trim();
      if (!raw) return;
      setHistory([]);

      const pos = positionOverride || {
        position: 'relative',
        width: '100%',
        maxHeight: '75vh',
        isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
      };
      setPopoverPos(pos);

      const isMulti = raw.includes(' ');
      const synthetic = {
        token_id: `standalone-${raw}-${Date.now()}`,
        normalized_form: raw.toLowerCase(),
        surface_form: raw,
        is_word: true,
        status: 'new',
        strength: 0,
      };
      setIsExpression(isMulti);
      setSelectedToken(synthetic);
      _fetchEntry(raw.toLowerCase(), isMulti);
    },
    [_fetchEntry]
  );


  const back = useCallback(() => {
    requestRef.current += 1;
    cancel();
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      currentTermRef.current = prev.token.normalized_form;
      currentIsExprRef.current = prev.isExpression;
      currentLanguageRef.current = prev.token.language_code || languageCode;
      setSelectedToken(prev.token);
      setWordData(prev.wordData);
      setIsExpression(prev.isExpression);
      setNote(prev.note || '');
      setTranslation(prev.translation || '');
      setTranslationSaved(!!prev.translationSaved);
      return h.slice(0, -1);
    });
  }, [cancel, setWordData, languageCode]);

  const refreshCurrent = useCallback(async () => {
    if (currentTermRef.current) {
      await _fetchEntry(currentTermRef.current, currentIsExprRef.current, currentLanguageRef.current);
    }
  }, [_fetchEntry]);

  return {
    // Entry points
    open,
    openForTerm,
    openStandalone,
    close,
    back,
    refreshCurrent,
    // State (read)
    selectedToken,
    wordData,
    wordLoading,
    popoverPos,
    isExpression,
    canGoBack: history.length > 0,
    historyDepth: history.length,
    // Editable state (read/write — the popover UI binds to these)
    note,
    setNote,
    translation,
    setTranslation,
    translationSaved,
    setTranslationSaved,
    hasCachedAudio,
    setHasCachedAudio,
    isNewEntry,
    setIsNewEntry,
    // Escape hatch for pages that need to patch wordData optimistically
    setWordData,
  };
}
